import React, { useState } from 'react'

import { formatUnits } from 'ethers/lib/utils'
import { DateTime } from 'luxon'
import classNames from 'classnames'

import { SymbiosisButton } from '~/components/SymbiosisButton'
import { AboutLockInfo } from '~/components/AboutLockInfo'
import { renderAmount } from '~/lib/helpers'
import { isLockDateExpired } from '~/lib/isLockDateExpired'
import { useWallet } from '~/lib/useWallet'
import { CONFIG } from '~/lib/config'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'
import type { Lock } from '~/lib/useLockDetails'

import { LockInfo } from './LockInfo'
import { useClaimLock } from './useClaimLock'
import { useWithdrawLock } from './useWithdrawLock'

import ChevronIcon from './assets/chevron.svg'

import styles from './AboutLock.pcss'
import { useVESISParams } from '~/hooks/useVESISParams'
import PageDescription from '~/components/PageDescription'

interface Props {
	disabled: boolean
	lock: Lock
	onClaimSuccess: () => void
}

export function AboutLock({ disabled, lock, onClaimSuccess }: Props) {
	const { config } = useVESISParams()

	const vesisChainId = config.chain.id

	const wallet = useWallet()

	const isWrongChain = !wallet.isCurrentChain(vesisChainId)

	const [showAbout, setShowAbout] = useState(false)

	const { isLoading: claiming, mutate: claim } = useClaimLock()
	const { isLoading: withdrawing, mutate: withdraw } = useWithdrawLock()

	const amount = formatUnits(lock.amount)
	const rewards = formatUnits(lock.rewards)
	const availableToClaim = formatUnits(lock.availableToClaim)

	const expiresDate = DateTime.fromSeconds(lock.end.toNumber())

	const isUnlock = isLockDateExpired(lock.end.toNumber())
	const hasRewards = !lock.availableToClaim.isZero()
	const canWithdraw = !lock.amount.isZero() && isUnlock && !hasRewards

	let claimButtonText
	if (!hasRewards) {
		claimButtonText = 'Nothing to claim'
	} else if (claiming) {
		claimButtonText = 'Claiming...'
	} else {
		claimButtonText = 'Claim Rewards'
	}

	const onWithdrawClick = () => {
		withdraw()
	}

	const onClaimClick = () => {
		claim(undefined, {
			onSuccess() {
				onClaimSuccess()
			},
		})
	}

	const claimDisabled = claiming || !hasRewards || disabled

	return (
		<div className={styles.container}>
			<PageDescription>
				{`SIS -> veSIS`}
				<br />
				Exclusive rewards for SIS holders.
			</PageDescription>
			<LockInfo
				amount={amount}
				expiresDate={expiresDate}
				isUnlock={isUnlock}
				rewards={rewards}
			/>

			<div className={styles.content}>
				<div
					className={classNames(
						styles.title,
						canWithdraw && styles.withdraw
					)}
				>
					{canWithdraw
						? 'Amount available for Unlock'
						: 'Rewards Available to Claim:'}
				</div>
				{!canWithdraw && (
					<div className={styles.value}>
						<span>{renderAmount(availableToClaim, 6)}</span>
						<span>SIS</span>
					</div>
				)}

				{!isUnlock && (
					<button
						className={classNames(
							styles.aboutLockButton,
							!showAbout && styles.aboutHidden
						)}
						type="button"
						onClick={() => {
							setShowAbout((value) => !value)
						}}
					>
						<span>MORE ABOUT LOCK</span>
						<ChevronIcon
							className={classNames(showAbout && styles.chevron)}
						/>
					</button>
				)}

				{showAbout && !isUnlock && (
					<div className={styles.about}>
						<AboutLockInfo
							hint="Your current veSIS balance"
							label="veSIS balance"
							postfix="veSIS"
							value={lock.currentVeBalance}
						/>
						<AboutLockInfo
							hint="Rewards depend on user`s part of total supply. This is an estimated value. It is calculated based on the total number of SIS tokens locked at the time of calculation and the amount of rewards allocated weekly. "
							label="Est. Weekly Rewards"
							postfix="SIS"
							value={lock.estimateWeaklyRewards}
						/>
						<AboutLockInfo
							hint="Weekly rewards * amount of remaining weeks. This is an estimated value. It is calculated based on the total number of SIS tokens locked at the time of calculation and the amount of rewards allocated weekly. "
							label="Est. Total Rewards"
							postfix="SIS"
							value={lock.totalRewards}
						/>
						<AboutLockInfo
							hint="Rewards are disbursed weekly, every Thursday at 00:00 UTC. They accumulate over a full week. For instance, if you lock SIS tokens on a Friday, the initial reward from that lock will be accessible in 13 days, rather than 6."
							label="Upcoming Reward Distribution"
							postfix=""
							value={new Date(
								lock.nextRewardAt.mul(1000).toNumber()
							).toDateString()}
						/>
					</div>
				)}

				<div className={styles.buttons}>
					{isUnlock && !isWrongChain && (
						<SymbiosisButton
							className={styles.unlockButton}
							disabled={withdrawing || !canWithdraw || disabled}
							variant="black"
							onClick={onWithdrawClick}
						>
							{withdrawing ? 'Withdrawing...' : 'Unlock Amount'}
						</SymbiosisButton>
					)}

					{isWrongChain ? (
						<SwitchNetworkButton
							className={styles.switchNetwork}
							color="black"
							disabled={claimDisabled}
							onClick={onClaimClick}
							requiredChainId={vesisChainId}
						/>
					) : (
						<SymbiosisButton
							disabled={claimDisabled}
							variant="white"
							onClick={onClaimClick}
						>
							{claimButtonText}
						</SymbiosisButton>
					)}
				</div>
			</div>
		</div>
	)
}
